import { BrowserRouter, Routes, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AppLayout } from "./components/layout/AppLayout";
import { OrgLayout } from "./components/layout/OrgLayout";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { OrgGuard } from "./components/auth/OrgGuard";
import { ApprovalGuard } from "./components/auth/ApprovalGuard";
import { SnackbarProvider } from "./components/common/SnackbarProvider";
import { HealthCheck } from "./pages/HealthCheck";
import { LoginPage } from "./pages/Auth/LoginPage";
import { SignupPage } from "./pages/Auth/SignupPage";
import { ResetPasswordPage } from "./pages/Auth/ResetPasswordPage";
import { SettingsPage } from "./pages/Settings/SettingsPage";
import { OnboardingPage } from "./pages/Onboarding/OnboardingPage";
import { OrgDashboardPage } from "./pages/Organization/OrgDashboardPage";
import { OrgSettingsPage } from "./pages/Organization/OrgSettingsPage";
import { OrgMembersPage } from "./pages/Organization/OrgMembersPage";
import { OrgTeamsPage } from "./pages/Organization/OrgTeamsPage";
import { AdminPromptsPage } from "./pages/Admin/AdminPromptsPage";
import { AdminDashboardPage } from "./pages/Admin/AdminDashboardPage";
import { AdminOrgsPage } from "./pages/Admin/AdminOrgsPage";
import { AdminOrgDetailPage } from "./pages/Admin/AdminOrgDetailPage";
import { AdminUsersPage } from "./pages/Admin/AdminUsersPage";
import { AdminHealthPage } from "./pages/Admin/AdminHealthPage";
import { AdminKnowledgeBasePage } from "./pages/Admin/AdminKnowledgeBasePage";
import { DocumentsPage } from "./pages/Documents/DocumentsPage";
import { ChatPage } from "./pages/Chat/ChatPage";
import { TranscriptsPage } from "./pages/Transcripts/TranscriptsPage";
import { OrgAuditLogPage } from "./pages/Organization/OrgAuditLogPage";
import { AnalyticsPage } from "./pages/Analytics/AnalyticsPage";
import { DashboardPage } from "./pages/Dashboard/DashboardPage";

function NotFound() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        404 - Page Not Found
      </Typography>
      <Typography sx={{
        color: "text.secondary"
      }}>
        The page you're looking for doesn't exist.
      </Typography>
    </Box>
  );
}

function ErrorFallback() {
  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h5" gutterBottom>
        Something went wrong
      </Typography>
      <Typography sx={{
        color: "text.secondary"
      }}>
        An unexpected error occurred. Please refresh the page.
      </Typography>
    </Box>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <SnackbarProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes — ApprovalGuard wraps AppLayout so pending/rejected
              users see a full-screen status page with no sidebar or app chrome. */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ApprovalGuard>
                  <AppLayout />
                </ApprovalGuard>
              </ProtectedRoute>
            }
          >
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route
              index
              element={
                <OrgGuard>
                  <DashboardPage />
                </OrgGuard>
              }
            />
            <Route
              path="chat"
              element={
                <OrgGuard>
                  <ChatPage />
                </OrgGuard>
              }
            />
            <Route
              path="chat/:conversationId"
              element={
                <OrgGuard>
                  <ChatPage />
                </OrgGuard>
              }
            />
            <Route
              path="transcripts"
              element={
                <OrgGuard>
                  <TranscriptsPage />
                </OrgGuard>
              }
            />
            <Route
              path="documents"
              element={
                <OrgGuard>
                  <DocumentsPage />
                </OrgGuard>
              }
            />
            <Route
              path="analytics"
              element={
                <OrgGuard>
                  <AnalyticsPage />
                </OrgGuard>
              }
            />
            <Route
              path="organization"
              element={
                <OrgGuard>
                  <OrgLayout />
                </OrgGuard>
              }
            >
              <Route index element={<OrgDashboardPage />} />
              <Route path="settings" element={<OrgSettingsPage />} />
              <Route path="members" element={<OrgMembersPage />} />
              <Route path="teams" element={<OrgTeamsPage />} />
              <Route path="audit-logs" element={<OrgAuditLogPage />} />
            </Route>
            <Route path="admin" element={<AdminDashboardPage />} />
            <Route path="admin/prompts" element={<AdminPromptsPage />} />
            <Route path="admin/organizations" element={<AdminOrgsPage />} />
            <Route
              path="admin/organizations/:orgId"
              element={<AdminOrgDetailPage />}
            />
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="admin/health" element={<AdminHealthPage />} />
            <Route
              path="admin/knowledge-base"
              element={<AdminKnowledgeBasePage />}
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="health" element={<HealthCheck />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </SnackbarProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
